/**
 * Environment Configuration
 *
 * Centralized environment variable management for Azure Static Web Apps.
 * All environment variables must be prefixed with VITE_ to be exposed to the client.
 *
 * Azure App Settings:
 * - Configure in Azure Portal > Static Web App > Configuration > Application settings
 * - Variables are automatically injected during build time
 * - Use the same names as in .env.example
 */

/**
 * Environment types
 */
export type Environment = 'development' | 'staging' | 'production'

/**
 * Application configuration interface
 */
export interface AppConfig {
  /** Current environment */
  env: Environment

  /** Is development environment */
  isDevelopment: boolean

  /** Is production environment */
  isProduction: boolean

  /** Backend API base URL */
  apiBaseUrl: string

  /** Sentry DSN for error tracking (optional) */
  sentryDsn?: string

  /** Application version */
  appVersion: string

  /** Enable Sentry error tracking */
  enableSentry: boolean

  /** Enable analytics */
  enableAnalytics: boolean
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key]

  if (value === undefined || value === '') {
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Missing required environment variable: ${key}`)
  }

  return value
}

/**
 * Get optional environment variable
 */
function getOptionalEnvVar(key: string, defaultValue: string = ''): string {
  return import.meta.env[key] || defaultValue
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, defaultValue: boolean = false): boolean {
  const value = import.meta.env[key]

  if (value === undefined || value === '') {
    return defaultValue
  }

  return value === 'true' || value === '1'
}

/**
 * Determine current environment
 */
function getEnvironment(): Environment {
  const nodeEnv = import.meta.env.MODE

  // Check if explicitly set
  if (nodeEnv === 'production') return 'production'
  if (nodeEnv === 'staging') return 'staging'
  if (nodeEnv === 'development') return 'development'

  // Default based on MODE
  return nodeEnv === 'production' ? 'production' : 'development'
}

/**
 * Validate required environment variables
 */
function validateEnv(): void {
  const errors: string[] = []

  // Required variables
  if (!import.meta.env.VITE_API_BASE_URL) {
    errors.push('VITE_API_BASE_URL is required')
  }

  // Validate API URL format
  const apiUrl = import.meta.env.VITE_API_BASE_URL
  if (apiUrl && !apiUrl.match(/^https?:\/\/.+/)) {
    errors.push('VITE_API_BASE_URL must be a valid HTTP(S) URL')
  }

  if (errors.length > 0) {
    throw new Error(`Environment validation failed:\n${errors.map(e => `  - ${e}`).join('\n')}`)
  }
}

/**
 * Load and validate environment configuration
 */
function loadConfig(): AppConfig {
  // Validate environment first
  if (import.meta.env.PROD) {
    validateEnv()
  }

  const env = getEnvironment()

  return {
    env,
    isDevelopment: env === 'development',
    isProduction: env === 'production',
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000'),
    sentryDsn: getOptionalEnvVar('VITE_SENTRY_DSN'),
    appVersion: getOptionalEnvVar('VITE_APP_VERSION', '1.0.0'),
    enableSentry: getBooleanEnvVar('VITE_ENABLE_SENTRY', env === 'production'),
    enableAnalytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS', false),
  }
}

/**
 * Application configuration instance
 */
export const config: AppConfig = loadConfig()

/**
 * Log configuration in development
 */
if (config.isDevelopment) {
  console.log('[Config] Environment:', config.env)
  console.log('[Config] API Base URL:', config.apiBaseUrl)
  console.log('[Config] App Version:', config.appVersion)
  console.log('[Config] Sentry Enabled:', config.enableSentry)
  console.log('[Config] Sentry DSN:', config.sentryDsn ? '[CONFIGURED]' : '[NOT SET]')
}

/**
 * Export individual config values for convenience
 */
export const {
  env,
  isDevelopment,
  isProduction,
  apiBaseUrl,
  sentryDsn,
  appVersion,
  enableSentry,
  enableAnalytics,
} = config

/**
 * Default export
 */
export default config
